package ui.hardware.detail

import androidx.compose.runtime.*
import androidx.compose.ui.platform.*
import api.*
import common.frontend.api.*
import provider.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.compose.navigation.viewmodel.*
import tools.*


interface HardwareDetailScope {

    val vm: HardwareDetailVM
    val action: (HardwareDetailVM.Action) -> Unit
    val nav: Navigation
    val login: Io.User
    val args: HardwareDetailArgs
    val uriHandler: UriHandler

    companion object {

        @Composable
        fun rememberScope(): HardwareDetailScope {
            val account = LocalAccountProvider.current
            val nav = LocalNavigation.current
            val vm = nav.viewModel(HardwareDetailVM::class)
            val args = LocalNavigationArgs.current.args<HardwareDetailArgs>()
            val action = HardwareDetailVM.Action.rememberAction(vm)
            val externals = LocalExternalsProvider.current
            return remember {
                val uriHandler = object : UriHandler {
                    override fun openUri(uri: String) {
                        externals.openInBrowser(uri)
                    }
                }
                object : HardwareDetailScope {
                    override val vm: HardwareDetailVM
                        get() = vm
                    override val action: (HardwareDetailVM.Action) -> Unit
                        get() = action
                    override val nav: Navigation
                        get() = nav
                    override val login: Io.User
                        get() = account.current!!
                    override val args: HardwareDetailArgs
                        get() = args
                    override val uriHandler: UriHandler
                        get() = uriHandler
                }.also {
                    vm.init(it)
                }
            }
        }
    }

    class Wrapper(
        val data: Io.Hardware,
        var canEdit: Boolean,
        var canDelete: Boolean,
    ) {
        val _change = mutableStateOf(false)
        val change: State<Boolean> get() = _change

        val name = EFieldTextString(data::name, onChange = {
            makeChange()
        })
        val image = EFieldRegularLong(data::image, onChange = {
            makeChange()
        })
        val canApps = EFieldMaskInt(data::capability, HardwareCapability.APPS.mask, onChange = {
            makeChange()
        })
        val canFHD = EFieldMaskInt(data::capability, HardwareCapability.VIDEO_FHD.mask, onChange = {
            makeChange()
        })
        val canUHD = EFieldMaskInt(data::capability, HardwareCapability.VIDEO_UHD.mask, onChange = {
            makeChange()
        })

        init {
            // hardware created before a language was added has no translation row for it -> add empty one,
            // otherwise the language tab has no fields and the translation cannot be filled in
            val present = data.translations.map { LanguageType[it.lang] }.toSet()
            val missing = LanguageType.all.filter { it !in present }
            if (missing.isNotEmpty()) {
                data.translations += missing.map { Io.Hardware.Translation(lang = it.type) }
            }
        }

        val subtitles = data.translations
            .associateBy(
                { LanguageType[it.lang] },
                { EFieldTextString(it::subtitle, onChange = { makeChange() }) })

        val description = data.translations
            .associateBy(
                { LanguageType[it.lang] },
                { EFieldTextString(it::description, onChange = { makeChange() }) })

        private val fields
            get() = listOf(
                name,
                image,
                canApps,
                canFHD,
                canUHD
            ) + subtitles.values + description.values

        fun makeChange() {
            _change.value = fields.any { it.change }
        }

        fun confirmChanges() {
            fields.forEach {
                it.applyChanges()
            }

            makeChange()
        }
    }
}
